import { inject, signal, Signal } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { Maybe } from 'graphql/jsutils/Maybe';

import {
  CreateSiteInvoiceableInput,
  GqlService,
  UpdateSiteInvoiceableInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchAPIRequest } from '@shared/utils';
import { gqlToPromise } from '@shared/utils/gql-to-promise';

export const injectSiteBudgetSiteInvoiceables = ({
  budgetVersionId,
  enabled = signal(true),
  protocolVersionId,
  siteId,
}: {
  budgetVersionId: Signal<Maybe<string>>;
  enabled?: Signal<boolean>;
  protocolVersionId: Signal<Maybe<string>>;
  siteId: Signal<Maybe<string>>;
}) => {
  const mainQuery = inject(MainQuery);
  const gqlService = inject(GqlService);

  const trialId = mainQuery.trialKey();

  return injectQuery(() => ({
    enabled: enabled(),
    queryFn: () => {
      return gqlToPromise(() =>
        gqlService.listSiteInvoiceables$({
          patient_protocol_version_id: protocolVersionId(),
          site_budget_version_id: budgetVersionId(),
          site_id: siteId(),
        })
      );
    },
    queryKey: ['siteBudgetInvoiceables', trialId, budgetVersionId(), protocolVersionId(), siteId()],
  }));
};

export const injectBatchCreateSiteInvoiceables = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: async ({ inputs }: { inputs: CreateSiteInvoiceableInput[] }) => {
      const { data, errors } = await batchAPIRequest(
        inputs,
        (input) => gqlService.batchCreateSiteInvoiceables$(input),
        50
      );

      if (errors.length) {
        throw errors;
      }

      return data;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['siteBudgetInvoiceables'],
        refetchType: 'active',
      });
    },
  }));
};

export const injectBatchUpdateSiteInvoiceables = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: async ({ inputs }: { inputs: UpdateSiteInvoiceableInput[] }) => {
      const { data, errors } = await batchAPIRequest(
        inputs,
        (input) => gqlService.batchUpdateSiteInvoiceables$(input),
        50
      );

      if (errors.length) {
        throw errors;
      }

      return data;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['siteBudgetInvoiceables'],
        refetchType: 'active',
      });
    },
  }));
};
